import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import {
  Package,
  LogOut,
  ChevronLeft,
  Shield,
  Truck,
  Calendar,
  Hash,
  Download,
  Info,
  HelpCircle,
  CheckCircle2,
  XCircle,
  Headphones,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  FileText,
  Tag,
  BadgeCheck,
} from "lucide-react";
import { Order } from "@/types";
import { useCustomerContext } from "@/context/CustomerContext";
import { useAuth } from "@/context/AuthContext";
import { useGuestContext } from "@/context/GuestContext";
import { createReturnRequest } from "@/store/slices/returnChatSlice";
import { AppDispatch } from "@/store/store";

const formatDate = (dateString: string) =>
  new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(dateString));

const Skeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`tw-relative tw-overflow-hidden tw-bg-[#1c1c1e] tw-rounded-md ${className}`}>
    <motion.div
      className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-transparent tw-via-white/5 tw-to-transparent"
      initial={{ x: "-100%" }}
      animate={{ x: "100%" }}
      transition={{
        duration: 1.6,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  </div>
);

const OrderDetailsSkeleton: React.FC = () => (
  <div className="tw-bg-[#131313] tw-min-h-screen tw-p-6 md:tw-p-10 tw-font-sans">
    {/* Header Skeleton */}
    <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end tw-justify-between tw-pb-8 tw-gap-4">
      <div className="tw-space-y-4 tw-flex-1">
        <Skeleton className="tw-h-3 tw-w-40" />
        <Skeleton className="tw-h-10 tw-w-64 md:tw-h-12" />
        <div className="tw-flex tw-items-center tw-gap-4 tw-mt-2">
          <Skeleton className="tw-h-6 tw-w-24 tw-rounded-md" />
          <Skeleton className="tw-h-4 tw-w-32" />
        </div>
      </div>
      <div className="tw-flex tw-items-center tw-gap-3">
        <Skeleton className="tw-h-10 tw-w-32 tw-rounded-md" />
        <Skeleton className="tw-h-10 tw-w-32 tw-rounded-md" />
        <Skeleton className="tw-h-10 tw-w-32 tw-rounded-md" />
      </div>
    </div>

    <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6 tw-mt-4">
      {/* Left Column Skeleton */}
      <div className="tw-col-span-1 lg:tw-col-span-2 tw-space-y-6">
        {/* Product Card Skeleton */}
        <div className="tw-bg-[#131313] tw-rounded-xl tw-border tw-border-white/5 tw-overflow-hidden">
          <div className="tw-px-6 tw-py-5 tw-border-b tw-border-gray-800/60">
            <Skeleton className="tw-h-4 tw-w-32" />
          </div>
          <div className="tw-p-6 tw-flex tw-flex-col sm:tw-flex-row tw-gap-6">
            <Skeleton className="tw-w-28 tw-h-28 tw-rounded-xl tw-flex-shrink-0" />
            <div className="tw-flex-1 tw-space-y-4">
              <Skeleton className="tw-h-6 tw-w-3/4" />
              <Skeleton className="tw-h-4 tw-w-1/2" />
              <Skeleton className="tw-h-8 tw-w-24" />
              <Skeleton className="tw-h-16 tw-w-full tw-rounded-md" />
            </div>
          </div>
        </div>
        {/* Activity Card Skeleton */}
        <div className="tw-bg-[#131313] tw-rounded-xl tw-border tw-border-white/5 tw-overflow-hidden">
          <div className="tw-px-6 tw-py-5 tw-border-b tw-border-gray-800/60">
            <Skeleton className="tw-h-4 tw-w-32" />
          </div>
          <div className="tw-p-6 tw-space-y-8">
            <div className="tw-flex tw-gap-4">
              <Skeleton className="tw-w-5 tw-h-5 tw-rounded-full tw-flex-shrink-0" />
              <div className="tw-space-y-2 tw-flex-1">
                <Skeleton className="tw-h-4 tw-w-24" />
                <Skeleton className="tw-h-3 tw-w-40" />
              </div>
            </div>
            <div className="tw-flex tw-gap-4">
              <Skeleton className="tw-w-5 tw-h-5 tw-rounded-full tw-flex-shrink-0" />
              <div className="tw-space-y-2 tw-flex-1">
                <Skeleton className="tw-h-4 tw-w-24" />
                <Skeleton className="tw-h-3 tw-w-40" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column Skeleton */}
      <div className="tw-col-span-1 tw-space-y-6">
        <div className="tw-bg-[#131313] tw-rounded-xl tw-border tw-border-white/5 tw-overflow-hidden">
          <div className="tw-px-6 tw-py-5 tw-border-b tw-border-gray-800/60">
            <Skeleton className="tw-h-4 tw-w-32" />
          </div>
          <div className="tw-p-6 tw-space-y-6">
            <Skeleton className="tw-h-12 tw-w-full tw-rounded-md" />
            <Skeleton className="tw-h-10 tw-w-full tw-rounded-md" />
            <div className="tw-space-y-2">
              <Skeleton className="tw-h-3 tw-w-full" />
              <Skeleton className="tw-h-3 tw-w-2/3 tw-mx-auto" />
            </div>
          </div>
        </div>
        <div className="tw-bg-[#131313] tw-rounded-xl tw-border tw-border-white/5 tw-overflow-hidden">
          <div className="tw-px-6 tw-py-5 tw-border-b tw-border-gray-800/60">
            <Skeleton className="tw-h-4 tw-w-32" />
          </div>
          <div className="tw-p-6 tw-flex tw-gap-4">
            <Skeleton className="tw-w-10 tw-h-10 tw-rounded-full tw-flex-shrink-0" />
            <div className="tw-space-y-3 tw-flex-1">
              <Skeleton className="tw-h-5 tw-w-1/2" />
              <Skeleton className="tw-h-12 tw-w-full" />
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
);

const OrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { orders, isLoading, error: customerError } = useCustomerContext();
  const { loginType, logout } = useAuth();
  const { returnOnboardingData, fetchOnboardingData, isLoading: contextLoading, error: guestError } = useGuestContext();

  const isGuest = loginType === 'token';
  const [expandedPolicyId, setExpandedPolicyId] = useState<string | null>(null);

  // Trigger fetch on mount
  useEffect(() => {
    if (id) {
      fetchOnboardingData(id);
    }
  }, [id, fetchOnboardingData]);

  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [isInitiating, setIsInitiating] = useState(false);

  // Auto-select if only 1 product
  useEffect(() => {
    if (isGuest && returnOnboardingData?.product_info?.length === 1) {
      setSelectedProduct(returnOnboardingData.product_info[0].id);
    }
  }, [isGuest, returnOnboardingData]);

  const handleStartReturn = async () => {
    if (!returnOnboardingData || !selectedProduct) return;

    const existingReturn = returnOnboardingData.returns?.find((r: any) => r.product_id === selectedProduct);
    const status = existingReturn?.status;

    if (status === 'approved') {
      // Direct navigation to fix-issue for approved returns
      navigate(`/customer/orders/${id}/fix-issue?productId=${selectedProduct}`);
      return;
    }

    if (status === 'processing' || status === 'shipped' || status === 'delivered') {
      navigate(`/customer/orders/${id}/track-status?productId=${selectedProduct}`);
      return;
    }

    if (status === 'requested') {
      navigate(`/customer/return-chat/${id}?productId=${selectedProduct}`);
      return;
    }

    setIsInitiating(true);
    try {
      // Trigger the backend return request creation
      const resultAction = await dispatch(createReturnRequest({
        order_id: returnOnboardingData.order_details.id,
        product_id: selectedProduct
      }));

      if (createReturnRequest.fulfilled.match(resultAction)) {
        // Redirect to chatbot on success
        navigate(`/customer/return-chat/${returnOnboardingData.order_details.id}?productId=${selectedProduct}`);
      } else {
        throw new Error("Failed to initialize return request");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to initialize return request. Please try again.");
    } finally {
      setIsInitiating(false);
    }
  };

  const order = orders.find((o: Order) => o.order_id === id);

  // ── Loading ── (Check if returnOnboardingData has full details)
  const isGuestDataLoading = isGuest && (!returnOnboardingData || !returnOnboardingData.order_details);
  const apiError = isGuest ? guestError : customerError;

  // ── Auto-Redirect on Error ──
  useEffect(() => {
    if (apiError && !isLoading && !contextLoading && !returnOnboardingData?.order_details && !order) {
      toast.error(`${apiError}. Redirecting to login...`, { id: 'api-error' });
      const timer = setTimeout(() => {
        const guestSlug = localStorage.getItem('merchant_portal_slug');
        if (isGuest) {
          logout();
          navigate(`/${guestSlug}/return-exchange`);
        } else {
          navigate("/customer-login");
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [apiError, isLoading, contextLoading, returnOnboardingData, order, isGuest, logout, navigate]);

  if (isLoading || isGuestDataLoading || contextLoading) {
    return <OrderDetailsSkeleton />;
  }

  // ── Guest Render ──
  if (isGuest && returnOnboardingData?.order_details) {
    const { order_details, product_info, shipping_details, activity_timeline, returns } = returnOnboardingData;

    // Fallback date if not present
    const placedDate = order_details?.placed_at || "2025-10-06T00:00:00Z";
    const productInfo = (product_info || []).filter((p: any) => !p.is_replacement);
    const isMultiple = productInfo.length > 1;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="tw-min-h-screen tw-p-6 md:tw-p-10 tw-font-sans"
      >
        {/* Top Breadcrumb & Actions */}
        <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end tw-justify-between tw-pb-8 tw-gap-4">
          <div className="tw-space-y-3">
            <div className="tw-text-xs tw-font-semibold tw-tracking-widest tw-uppercase tw-text-gray-500">
              ORDERS <span className="tw-mx-1">&gt;</span> <span className="tw-text-[#00C5FB]">#{order_details.order_number}</span>
            </div>
            <h1 className="tw-text-3xl md:tw-text-4xl tw-font-bold tw-text-white tw-tracking-tight">
              Order #{order_details.order_number}
            </h1>
            <div className="tw-flex tw-items-center tw-gap-4 tw-mt-2">
              <div className="tw-flex tw-items-center tw-gap-1.5 tw-bg-[#3F2B20] tw-text-[#FDBA74] tw-px-2.5 tw-py-1 tw-rounded-md tw-text-xs tw-font-semibold">
                <svg className="tw-w-3.5 tw-h-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {order_details.status || "Delivered"}
              </div>
              <span className="tw-text-sm tw-text-gray-400">Placed on {formatDate(placedDate)}</span>
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-3">
            {/* <button className="tw-flex tw-items-center tw-gap-2 tw-bg-[#131313] hover:tw-bg-[#1c1c1e] tw-border tw-border-white/5 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-gray-200 tw-transition-colors">
              <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
              Share Details
            </button>
            <button className="tw-flex tw-items-center tw-gap-2 tw-bg-[#131313] hover:tw-bg-[#1c1c1e] tw-border tw-border-white/5 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-gray-200 tw-transition-colors">
              <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Need Help?
            </button> */}
            <button
              onClick={logout}
              className="tw-flex tw-items-center tw-gap-2 tw-bg-[#131313] hover:tw-bg-red-900/20 hover:tw-text-red-400 tw-border tw-border-white/5 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-gray-200 tw-transition-colors"
            >
              <LogOut className="tw-w-4 tw-h-4" />
              Sign Out
            </button>
          </div>
        </div>

        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6 tw-mt-4">

          {/* Main Content Column */}
          <div className="tw-col-span-1 lg:tw-col-span-2 tw-space-y-6">

            {/* Product Information Card */}
            <div className="tw-bg-[#131313] tw-border tw-border-white/5 tw-rounded-xl tw-overflow-hidden">
              <div className="tw-px-6 tw-py-5 tw-flex tw-justify-between tw-items-center tw-border-b tw-border-gray-800/60">
                <h3 className="tw-text-xs tw-font-bold tw-tracking-widest tw-uppercase tw-text-gray-400">Product Information</h3>
                <span className="tw-text-xs tw-font-medium tw-text-gray-300">{order_details.total_items} {order_details.total_items === 1 ? 'Item' : 'Items'}</span>
              </div>
              <div className="tw-divide-y tw-divide-gray-800/60 tw-max-h-[470px] tw-overflow-y-auto [&::-webkit-scrollbar]:tw-w-2 [&::-webkit-scrollbar-thumb]:tw-rounded-full [&::-webkit-scrollbar-thumb]:tw-bg-[#38383B] [&::-webkit-scrollbar-track]:tw-bg-[#131313] hover:[&::-webkit-scrollbar-thumb]:tw-bg-[#4A4A4D] tw-pr-1">
                {productInfo.map((product: any) => (
                  <div
                    key={product.id}
                    onClick={() => isMultiple && setSelectedProduct(product.id)}
                    className={`tw-p-6 tw-flex tw-flex-col sm:tw-flex-row tw-gap-6 tw-transition-colors ${isMultiple ? 'tw-cursor-pointer hover:tw-bg-[#1c1c1e]' : ''} ${selectedProduct === product.id && isMultiple ? 'tw-bg-[#1c1c1e]' : ''}`}
                  >

                    {/* Radio Button (only if multiple) */}
                    {isMultiple && (
                      <div className="tw-flex-shrink-0 tw-flex tw-items-center sm:tw-items-start sm:tw-pt-12">
                        <div className={`tw-w-5 tw-h-5 tw-rounded-full tw-border-2 tw-flex tw-items-center tw-justify-center tw-transition-colors ${selectedProduct === product.id ? 'tw-border-[#00C5FB] tw-bg-[#00C5FB]' : 'tw-border-gray-500 tw-bg-transparent'}`}>
                          {selectedProduct === product.id && <div className="tw-w-2 tw-h-2 tw-bg-black tw-rounded-full"></div>}
                        </div>
                      </div>
                    )}

                    <div className="tw-w-28 tw-h-28 tw-bg-[#E5E5E5] tw-rounded-xl tw-overflow-hidden tw-flex-shrink-0">
                      {product.image ? (
                        <img src={product.image} alt={product.title} className="tw-w-full tw-h-full tw-object-cover" />
                      ) : (
                        <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
                          <Package className="tw-text-gray-400 tw-w-10 tw-h-10" />
                        </div>
                      )}
                    </div>
                    <div className="tw-flex-1 tw-space-y-3">
                      <div>
                        <h4 className="tw-text-lg tw-font-bold tw-text-gray-100">{product.title}</h4>
                        {product.variant && <p className="tw-text-sm tw-text-gray-400 tw-mt-0.5">{product.variant}</p>}
                      </div>
                      <div className="tw-flex tw-items-end tw-gap-3">
                        <p className="tw-text-xl tw-font-extrabold tw-text-white">₹{product.price?.toLocaleString()}</p>
                      </div>

                      <div className="tw-mt-4 tw-bg-[#222225] tw-px-4 tw-py-3 tw-rounded-md tw-flex tw-w-fit  tw-items-start tw-gap-3">
                        <svg className="tw-w-4 tw-h-4 tw-text-[#00C5FB] tw-flex-shrink-0 tw-mt-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <p className={`tw-text-xs tw-w-auto ${product.eligible_until && new Date(product.eligible_until) < new Date() ? 'tw-text-red-400' : 'tw-text-gray-300'}`}>
                          {product.eligible_until 
                            ? new Date(product.eligible_until) < new Date()
                              ? `Return period expired on ${product.eligible_until}.`
                              : `Eligible for returns until ${product.eligible_until}. ` 
                            : 'Eligible for return based on standard policy. '}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Latest Activity Timeline Card */}
            {/* <div className="tw-bg-[#131313] tw-border tw-border-white/5 tw-rounded-xl tw-overflow-hidden">
              <div className="tw-px-6 tw-py-5 tw-border-b tw-border-gray-800/60 tw-flex tw-justify-between tw-items-center">
                <h3 className="tw-text-xs tw-font-bold tw-tracking-widest tw-uppercase tw-text-gray-400">Latest Activity</h3>
              </div>
              <div className="tw-p-6">
                <div className="tw-flex tw-flex-col tw-space-y-0 tw-relative">
                  <div className="tw-flex tw-gap-4 tw-relative tw-pb-8">
                    <div className="tw-absolute tw-left-[9px] tw-top-6 tw-bottom-[-8px] tw-w-0.5 tw-bg-gray-700"></div>
                    <div className="tw-relative tw-z-10 tw-w-5 tw-h-5 tw-mt-1 tw-rounded-full tw-border-2 tw-border-gray-700 tw-bg-[#131313] tw-flex-shrink-0"></div>
                    <div>
                      <h4 className="tw-text-gray-300 tw-font-semibold tw-text-sm">Order Placed</h4>
                      <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">
                        Purchased from website • {formatDate(placedDate)}
                      </p>
                    </div>
                  </div>

                  {(activity_timeline as any)?.delivered_at && (
                    <div className="tw-flex tw-gap-4 tw-relative tw-pb-8">
                      <div className="tw-absolute tw-left-[9px] tw-top-6 tw-bottom-[-8px] tw-w-0.5 tw-bg-gray-700"></div>
                      <div className="tw-relative tw-z-10 tw-w-5 tw-h-5 tw-mt-1 tw-rounded-full tw-border-2 tw-border-gray-700 tw-bg-[#131313] tw-flex-shrink-0"></div>
                      <div>
                        <h4 className="tw-text-gray-300 tw-font-semibold tw-text-sm">Order Received</h4>
                        <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">{formatDate((activity_timeline as any).delivered_at)}</p>
                      </div>
                    </div>
                  )}

                  // return specific logic based on selected product 
                  {(() => {
                    const activeReturn = returns?.find((r: any) => r.product_id === selectedProduct);
                    if (!activeReturn) return null;

                    const status = activeReturn.status?.toLowerCase();
                    const category = activeReturn.category?.toLowerCase();
                    const refundMethod = (activeReturn as any).selected_refund_method || (activeReturn as any).refund_type;

                    return (
                      <>
                        // REQUESTED STATE: Request Initiated
                        {(status === 'requested' || status === 'approved' || status === 'processing') && (
                          <div className="tw-flex tw-gap-4 tw-relative tw-pb-8">
                            <div className="tw-absolute tw-left-[9px] tw-top-6 tw-bottom-[-8px] tw-w-0.5 tw-bg-[#00C5FB]"></div>
                            <div className="tw-relative tw-z-10 tw-w-5 tw-h-5 tw-mt-1 tw-rounded-full tw-bg-[#00C5FB] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                              <svg className="tw-w-3 tw-h-3 tw-text-black" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                            </div>
                            <div>
                              <h4 className="tw-text-white tw-font-semibold tw-text-sm">Request Initiated</h4>
                              <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">Submitted by customer • {formatDate(activeReturn.created_at)}</p>
                            </div>
                          </div>
                        )}

                        // APPROVED STATE: Approved + Resolution Pending
                        {(status === 'approved' || status === 'processing') && (
                          <div className="tw-flex tw-gap-4 tw-relative tw-pb-8">
                            <div className="tw-absolute tw-left-[9px] tw-top-6 tw-bottom-[-8px] tw-w-0.5 tw-bg-[#00C5FB]"></div>
                            <div className="tw-relative tw-z-10 tw-w-5 tw-h-5 tw-mt-1 tw-rounded-full tw-bg-[#00C5FB] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                              <svg className="tw-w-3 tw-h-3 tw-text-black" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                            </div>
                            <div>
                              <h4 className="tw-text-white tw-font-semibold tw-text-sm">Request Approved</h4>
                              <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">Automated Triage Complete • {formatDate((activeReturn as any).updated_at || activeReturn.created_at)}</p>
                              {status === 'approved' && (
                                <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-[#00C5FB]/80 tw-font-medium">
                                  <div className="tw-w-1.5 tw-h-1.5 tw-bg-[#00C5FB] tw-rounded-full tw-animate-ping"></div>
                                  Resolution is Pending
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        // PROCESSING STATE: category based details
                        {status === 'processing' && (
                          <div className="tw-flex tw-gap-4 tw-relative">
                            <div className="tw-relative tw-z-10 tw-w-5 tw-h-5 tw-mt-1 tw-rounded-full tw-bg-[#00C5FB] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-shadow-[0_0_15px_rgba(0,197,251,0.3)]">
                              <div className="tw-w-2 tw-h-2 tw-bg-black tw-rounded-full tw-animate-pulse"></div>
                            </div>
                            <div>
                              <h4 className="tw-text-white tw-font-semibold tw-text-sm">
                                {category === 'refund' ? 'Refund Processing' : category === 'exchange' ? 'Exchange Processing' : 'Return Processing'}
                              </h4>
                              <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">
                                {category === 'refund'
                                  ? `Initiated via ${refundMethod?.replace('_', ' ') || 'original source'}`
                                  : category === 'exchange'
                                    ? 'Preparing replacement shipment'
                                    : 'Processing resolution details'}
                              </p>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div> */}
          </div>

          {/* Right Action / Info Column */}
          <div className="tw-col-span-1 tw-space-y-6">

            {/* Next Actions Card (only highlighted big button if single item) */}
            <div className="tw-bg-[#131313] tw-border tw-border-white/5 tw-rounded-xl tw-overflow-hidden">
              <div className="tw-px-6 tw-py-5 tw-border-b tw-border-gray-800/60">
                <h3 className="tw-text-xs tw-font-bold tw-tracking-widest tw-uppercase tw-text-gray-400">Next Actions</h3>
              </div>
              <div className="tw-p-6 tw-space-y-4">

                <button
                  disabled={!selectedProduct || isInitiating || (() => {
                    const activeReturn = returnOnboardingData?.returns?.find((r: any) => r.product_id === selectedProduct);
                    if (activeReturn?.status === 'manual_review') return true;
                    const currentProduct = returnOnboardingData?.product_info?.find((p: any) => p.id === selectedProduct);
                    const isExpired = currentProduct?.eligible_until ? new Date(currentProduct.eligible_until) < new Date() : false;
                    return !activeReturn && isExpired;
                  })()}
                  onClick={handleStartReturn}
                  className={`tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-colors tw-py-3.5 tw-rounded-md tw-font-bold tw-text-sm tw-border-none ${
                    selectedProduct && !isInitiating && !(() => {
                      const activeReturn = returnOnboardingData?.returns?.find((r: any) => r.product_id === selectedProduct);
                      if (activeReturn?.status === 'manual_review') return true;
                      const currentProduct = returnOnboardingData?.product_info?.find((p: any) => p.id === selectedProduct);
                      const isExpired = currentProduct?.eligible_until ? new Date(currentProduct.eligible_until) < new Date() : false;
                      return !activeReturn && isExpired;
                    })()
                    ? 'tw-bg-[#00C5FB] tw-text-black hover:tw-bg-cyan-500 tw-cursor-pointer'
                    : 'tw-bg-gray-700 tw-text-gray-400 tw-cursor-not-allowed'
                    }`}
                >
                  {isInitiating ? (
                    <div className="tw-w-5 tw-h-5 tw-border-2 tw-border-black/20 tw-border-t-black tw-rounded-full tw-animate-spin" />
                  ) : (
                    <>
                      {(() => {
                        const activeReturn = returnOnboardingData?.returns?.find((r: any) => r.product_id === selectedProduct);
                        const currentProduct = returnOnboardingData?.product_info?.find((p: any) => p.id === selectedProduct);
                        const isExpired = currentProduct?.eligible_until ? new Date(currentProduct.eligible_until) < new Date() : false;

                        if (!activeReturn && isExpired) return 'Return Period Expired';
                        if (!activeReturn) return 'Initiate Return';
                        if (activeReturn.status === 'approved') return 'View Return Options';
                        if (activeReturn.status === 'requested') return 'Continue with Request';
                        if (activeReturn.status === 'manual_review') return 'Under Manual Review';
                        if (activeReturn.status === 'processing' || activeReturn.status === 'shipped' || activeReturn.status === 'delivered') return 'Track Status';
                        return 'Initiate Return';
                      })()}
                      {(() => {
                        const activeReturn = returnOnboardingData?.returns?.find((r: any) => r.product_id === selectedProduct);
                        const currentProduct = returnOnboardingData?.product_info?.find((p: any) => p.id === selectedProduct);
                        const isExpired = currentProduct?.eligible_until ? new Date(currentProduct.eligible_until) < new Date() : false;
                        const hasActiveReturn = !!activeReturn;

                        const isActionable = (hasActiveReturn && activeReturn.status !== 'manual_review') || (!hasActiveReturn && !isExpired);
                        if (isActionable) {
                          return <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>;
                        }
                        return null;
                      })()}
                    </>
                  )}
                </button>

                {/* View Chat Button */}
                <button
                  disabled={!((activity_timeline as any)?.claim_submitted || returnOnboardingData?.returns?.some((r: any) => r.product_id === selectedProduct && r.submitted))}
                  onClick={() => navigate(`/customer/return-chat/${order_details.id}?productId=${selectedProduct}`)}
                  className={`tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-colors tw-py-3.5 tw-rounded-md tw-font-semibold tw-text-sm tw-border tw-border-white/5 
                    ${!((activity_timeline as any)?.claim_submitted || returnOnboardingData?.returns?.some((r: any) => r.product_id === selectedProduct && r.submitted))
                      ? 'tw-bg-gray-800 tw-text-gray-600 tw-cursor-not-allowed'
                      : 'tw-bg-[#131313] hover:tw-bg-[#1c1c1e] tw-text-white tw-cursor-pointer'
                    }`}
                >
                  <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                  View Chat
                </button>

                <p className="tw-text-[11px] tw-text-gray-500 tw-text-center tw-mt-4 tw-leading-relaxed">
                  Need more help? <a href="/contact-us" target="_blank" rel="noopener noreferrer" className="tw-text-cyan-400 tw-no-underline hover:tw-underline">Contact Executive Support</a><br />
                </p>
              </div>
            </div>

            {/* Shipping Details */}
            {shipping_details && (
              <div className="tw-bg-[#131313] tw-border tw-border-white/5 tw-rounded-xl tw-overflow-hidden">
                <div className="tw-px-6 tw-py-5 tw-border-b tw-border-gray-800/60">
                  <h3 className="tw-text-xs tw-font-bold tw-tracking-widest tw-uppercase tw-text-gray-400">Shipping Details</h3>
                </div>
                <div className="tw-p-6">
                  <div className="tw-flex tw-items-start tw-gap-4">
                    {/* <div className="tw-w-10 tw-h-10 tw-bg-[#1c1c1e] tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                      <svg className="tw-w-5 tw-h-5 tw-text-[#00C5FB]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.243-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    </div> */}
                    <div className="tw-space-y-4">
                      <div>
                        <h4 className="tw-text-base tw-font-bold tw-text-gray-100 tw-mb-1">{shipping_details.name}</h4>
                        <p className="tw-text-sm tw-text-gray-400 tw-leading-relaxed tw-line-clamp-3">
                          {[
                            shipping_details.address_line_1,
                            shipping_details.address_line_2,
                            `${shipping_details.city}, ${shipping_details.state} ${shipping_details.zip}`,
                            shipping_details.country
                          ].filter(Boolean).join(", ")}
                        </p>

                      </div>

                      {shipping_details.phone && (
                        <div className="tw-pt-3">
                          <h5 className="tw-text-xs tw-font-bold tw-tracking-widest tw-uppercase tw-text-gray-500 tw-mb-1">Phone Number</h5>
                          <p className="tw-text-sm tw-font-bold tw-text-gray-100">{shipping_details.phone}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </motion.div>

    );
  }

  // ── Not Found ──
  if (!order) {
    return (
      <div className="tw-bg-[#131313] tw-min-h-screen tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="tw-w-full tw-max-w-md tw-relative"
        >
          {/* Decorative Glows */}
          <div className="tw-absolute -tw-top-24 -tw-left-24 tw-w-72 tw-h-72 tw-bg-[#00C5FB]/10 tw-rounded-full tw-blur-[100px] tw-pointer-events-none" />
          <div className="tw-absolute -tw-bottom-24 -tw-right-24 tw-w-72 tw-h-72 tw-bg-[#00C5FB]/10 tw-rounded-full tw-blur-[100px] tw-pointer-events-none" />

          <div className="tw-bg-[#131313] tw-backdrop-blur-2xl tw-p-10 tw-rounded-3xl tw-border tw-border-white/5 tw-text-center tw-shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]">
            <div className="tw-w-24 tw-h-24 tw-bg-[#1c1c1e] tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-8 tw-border tw-border-white/5 tw-relative">
              <Package className="tw-w-10 tw-h-10 tw-text-gray-500" />
              <div className="tw-absolute tw-inset-0 tw-bg-[#00C5FB]/5 tw-rounded-full tw-animate-pulse" />
            </div>

            <h2 className="tw-text-3xl tw-font-bold tw-text-white tw-mb-4 tw-tracking-tight">Order Not Found</h2>
            <p className="tw-text-gray-400 tw-mb-10 tw-text-base tw-leading-relaxed">
              We couldn't locate the details for this order. It may have been archived or the link you followed is incorrect.
            </p>

            <div className="tw-space-y-4">
              <button
                onClick={() => isGuest ? navigate("/return-exchange") : navigate("/customer/orders")}
                className="tw-w-full tw-bg-[#00C5FB] tw-text-black tw-font-bold tw-py-4 tw-rounded-xl hover:tw-bg-cyan-400 tw-transition-all tw-border-none tw-cursor-pointer tw-text-sm tw-shadow-lg shadow-[#00C5FB]/20"
              >
                {isGuest ? "Return to Login" : "Back to Order History"}
              </button>

              <button
                onClick={() => window.open('/contact-us', '_blank')}
                className="tw-w-full tw-bg-transparent tw-text-gray-300 tw-font-semibold tw-py-3.5 tw-rounded-xl hover:tw-bg-white/5 tw-transition-all tw-border tw-border-white/10 tw-cursor-pointer tw-text-sm"
              >
                Contact Support
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  const purchaseDateStr = formatDate(order.created_at);
  const formattedOrderId = `ORD-${order.order_id.slice(-10).toUpperCase()}`;

  const policies = order.policy
    ? (Array.isArray(order.policy) ? order.policy : [order.policy])
    : [];

  const calculateTimeRemaining = (endDateStr: string) => {
    const diffMs = new Date(endDateStr).getTime() - Date.now();
    if (diffMs <= 0) return "Expired";
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    const diffYears = Math.floor(diffDays / 365);
    const remainingDays = diffDays % 365;
    const diffMonths = Math.floor(remainingDays / 30.4375);
    const finalDays = Math.round(remainingDays % 30.4375);

    let parts = [];
    if (diffYears > 0) parts.push(`${diffYears} Year${diffYears > 1 ? 's' : ''}`);
    if (diffMonths > 0) parts.push(`${diffMonths} Month${diffMonths > 1 ? 's' : ''}`);
    if (finalDays > 0 && diffYears === 0) parts.push(`${finalDays} Day${finalDays > 1 ? 's' : ''}`);

    return parts.join(", ");
  };

  const calculateCoverageDuration = (startDateStr: string, endDateStr: string) => {
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    const diffMonths = Math.round(diffDays / 30.4375);
    const diffYears = Math.round(diffDays / 365);

    if (diffYears >= 1 && Math.abs(diffDays - diffYears * 365) < 30) {
      return `${diffYears}-Year`;
    } else if (diffMonths >= 1) {
      return `${diffMonths}-Month`;
    } else {
      return `${diffDays}-Day`;
    }
  };

  const getLatestEndDate = () => {
    if (policies.length === 0) {
      const mockEndDate = new Date(order.created_at);
      mockEndDate.setFullYear(mockEndDate.getFullYear() + 5);
      return formatDate(mockEndDate.toISOString());
    }
    const dates = policies.map((p: any) => new Date(p.end_date).getTime());
    const maxDate = new Date(Math.max(...dates));
    return formatDate(maxDate.toISOString());
  };

  const handleDownloadAllCertificates = () => {
    let count = 0;
    policies.forEach((pol: any, idx: number) => {
      if (pol.certificate_url) {
        count++;
        setTimeout(() => {
          const link = document.createElement("a");
          link.href = pol.certificate_url;
          link.download = `${pol.policy_number || 'policy'}_certificate.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }, idx * 600);
      }
    });
    if (count === 0) {
      toast.error("No certificate URLs available to download.");
    } else {
      toast.success(`Starting download for ${count} certificate(s).`);
    }
  };

  const handleDownloadSingleCertificate = (url?: string, policyNum?: string) => {
    if (!url) {
      toast.error("Certificate URL not available");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening certificate...");
  };

  const getRenderablePlans = () => {
    if (policies.length > 0) {
      return policies.map((pol: any) => {
        let durationLabel = calculateCoverageDuration(pol.start_date, pol.end_date);
        let timeRemainingLabel = calculateTimeRemaining(pol.end_date);
        
        let title = "Product Protection";
        let description = "Covers damage or issues according to plan coverage.";
        let typeBadge = "ADD-ON";
        let icon = <Shield className="tw-w-5 tw-h-5 tw-text-orange-400" />;
        let iconBg = "tw-bg-orange-950/40";
        let isIncluded = false;

        let covered = ["Accidental drops, impacts, cracks, and drops", "Liquid damage and spills", "Structural defects"];
        let notCovered = ["Theft or mysterious disappearance", "Normal wear and tear", "Intentional damage"];

        if (pol.policy_type === 'shipping') {
          title = "Shipping Protection";
          description = "Covers loss or damage during transit until the product is delivered.";
          typeBadge = "INCLUDED";
          icon = <Truck className="tw-w-5 tw-h-5 tw-text-purple-400" />;
          iconBg = "tw-bg-purple-950/40";
          isIncluded = true;
          covered = ["Covers loss or damage during transit", "Delivered but missing / stolen packages", "Damage sustained in cargo or shipping containers"];
          notCovered = ["Post-delivery damage after safe receipt", "Items not shipped or cancelled before transit"];
        } else if (pol.policy_type === 'manufacturing') {
          title = `${durationLabel.replace('-', ' ')} Manufacturing Warranty`;
          description = "Covers manufacturing defects and workmanship issues under normal usage.";
          typeBadge = "INCLUDED";
          icon = <Shield className="tw-w-5 tw-h-5 tw-text-[#E3A045]" />;
          iconBg = "tw-bg-[#E3A045]/10";
          isIncluded = true;
          covered = [
            "Manufacturing defects in materials and workmanship",
            "Faulty hardware, parts, and assembly defects",
            "Structural or mechanical component failures",
            "Internal part malfunctions under normal usage"
          ];
          notCovered = [
            "Damage caused by accidents, drops, or impact",
            "Wear and tear from regular usage",
            "Damage caused by misuse or incorrect handling",
            "Cosmetic damage (scratches, dents, stains)",
            "External or environmental causes"
          ];
        } else if (pol.policy_type === 'extended' || pol.policy_type === 'manufacturing') {
          title = `${durationLabel.replace('-', ' ')} Manufacturing Warranty`;
          description = "Covers manufacturing defects and workmanship issues under normal usage.";
          typeBadge = "INCLUDED";
          icon = <Shield className="tw-w-5 tw-h-5 tw-text-[#E3A045]" />;
          iconBg = "tw-bg-[#E3A045]/10";
          isIncluded = true;
          covered = [
            "Manufacturing defects in materials and workmanship",
            "Faulty hardware, parts, and assembly defects",
            "Structural or mechanical component failures",
            "Internal part malfunctions under normal usage"
          ];
          notCovered = [
            "Damage caused by accidents, drops, or impact",
            "Wear and tear from regular usage",
            "Damage caused by misuse or incorrect handling",
            "Cosmetic damage (scratches, dents, stains)",
            "External or environmental causes"
          ];
        } else if (pol.policy_type === 'protection') {
          title = `${durationLabel.replace('-', ' ')} Accidental Damage Protection`;
          description = "Covers accidental damage such as drops, impact, cracks, or handling mishaps.";
          typeBadge = "ADD-ON";
          icon = <Shield className="tw-w-5 tw-h-5 tw-text-[#E3A045]" />;
          iconBg = "tw-bg-[#E3A045]/10";
          covered = [
            "Accidental drops, impacts, cracks, and breakage",
            "Liquid spills and ingress damage",
            "Cracked screens or damaged outer casings from falls"
          ];
          notCovered = [
            "Theft, burglary or mysterious disappearance",
            "Normal wear and tear and cosmetic blemishes",
            "Intentional or deliberate misuse"
          ];
        }

        if (pol.merchant_terms) {
          if (pol.policy_type === 'protection' && Array.isArray(pol.merchant_terms["Accidental Damage"])) {
            covered = pol.merchant_terms["Accidental Damage"];
          } else if (pol.policy_type === 'manufacturing' && Array.isArray(pol.merchant_terms["Manufacturing Warranty"])) {
            covered = pol.merchant_terms["Manufacturing Warranty"];
          } else if (pol.policy_type === 'extended' && Array.isArray(pol.merchant_terms["Extended Warranty"])) {
            covered = pol.merchant_terms["Extended Warranty"];
          } else if (pol.policy_type === 'extended' && Array.isArray(pol.merchant_terms["Manufacturing Warranty"])) {
            covered = pol.merchant_terms["Manufacturing Warranty"];
          } else if (pol.policy_type === 'shipping' && Array.isArray(pol.merchant_terms["Shipping Warranty"])) {
            covered = pol.merchant_terms["Shipping Warranty"];
          }
        }

        return {
          id: pol.id,
          policy_number: pol.policy_number,
          policy_type: pol.policy_type,
          title,
          description,
          typeBadge,
          icon,
          iconBg,
          isIncluded,
          coveragePeriod: `${formatDate(pol.start_date)} - ${formatDate(pol.end_date)}`,
          durationTag: durationLabel.replace('-', ' '),
          timeRemaining: timeRemainingLabel,
          statusLabel: new Date(pol.end_date).getTime() > Date.now() ? "Active Coverage" : "Expired",
          certificateUrl: pol.certificate_url,
          covered,
          notCovered,
          startDate: pol.start_date,
          endDate: pol.end_date
        };
      });
    }

    // Mock fallback matching the design screenshot
    const fallbackStartDate = order.created_at;
    const fallbackEndDate1 = new Date(new Date(fallbackStartDate).getTime() + 15 * 24 * 60 * 60 * 1000).toISOString();
    const fallbackEndDate2 = new Date(new Date(fallbackStartDate).setFullYear(new Date(fallbackStartDate).getFullYear() + 5)).toISOString();
    const fallbackEndDate3 = new Date(new Date(fallbackStartDate).setFullYear(new Date(fallbackStartDate).getFullYear() + 1)).toISOString();

    return [
      {
        id: "mock-shipping",
        policy_number: `POL-${order.order_id.slice(-6).toUpperCase()}-SHP`,
        policy_type: "shipping",
        title: "Shipping Protection",
        description: "Covers loss or damage during transit until the product is delivered.",
        typeBadge: "INCLUDED",
        icon: <Truck className="tw-w-5 tw-h-5 tw-text-purple-400" />,
        iconBg: "tw-bg-purple-950/40",
        isIncluded: true,
        coveragePeriod: `${formatDate(fallbackStartDate)} - ${formatDate(fallbackEndDate1)}`,
        durationTag: "15 Days",
        timeRemaining: "Transit Coverage",
        statusLabel: "Covered until delivery",
        certificateUrl: "",
        covered: [
          "Covers loss or damage during transit",
          "Delivered but missing / stolen packages",
          "Damage sustained in cargo or shipping containers"
        ],
        notCovered: [
          "Post-delivery damage after safe receipt",
          "Items not shipped or cancelled before transit"
        ],
        startDate: fallbackStartDate,
        endDate: fallbackEndDate1
      },
      {
        id: "mock-extended",
        policy_number: `MWR-${order.order_id.slice(-10).toUpperCase()}-01`,
        policy_type: "extended",
        title: "5-Year Manufacturing Warranty",
        description: "Covers manufacturing defects and workmanship issues under normal usage.",
        typeBadge: "INCLUDED",
        icon: <Shield className="tw-w-5 tw-h-5 tw-text-[#E3A045]" />,
        iconBg: "tw-bg-[#E3A045]/10",
        isIncluded: true,
        coveragePeriod: `${formatDate(fallbackStartDate)} - ${formatDate(fallbackEndDate2)}`,
        durationTag: "5 Years",
        timeRemaining: calculateTimeRemaining(fallbackEndDate2),
        statusLabel: "Active Coverage",
        certificateUrl: "",
        covered: [
          "Manufacturing defects in materials and workmanship",
          "Faulty zippers, locks, handles, and wheels",
          "Structural issues with the luggage shell",
          "Telescopic handle malfunctions",
          "Rivets, stitching, and fittings",
          "Any other issues arising from normal use"
        ],
        notCovered: [
          "Damage caused by accidents, drops, or impact",
          "Wear and tear from regular usage",
          "Damage caused by misuse or incorrect handling",
          "Cosmetic damage (scratches, dents, stains)",
          "Damage caused by airline or transport handling"
        ],
        startDate: fallbackStartDate,
        endDate: fallbackEndDate2
      },
      {
        id: "mock-protection",
        policy_number: `POL-${order.order_id.slice(-10).toUpperCase()}-ADP`,
        policy_type: "protection",
        title: "1-Year Accidental Damage Protection",
        description: "Covers accidental damage such as drops, impact, cracks, or handling mishaps.",
        typeBadge: "ADD-ON",
        icon: <Shield className="tw-w-5 tw-h-5 tw-text-[#E3A045]" />,
        iconBg: "tw-bg-[#E3A045]/10",
        isIncluded: false,
        coveragePeriod: `${formatDate(fallbackStartDate)} - ${formatDate(fallbackEndDate3)}`,
        durationTag: "1 Year",
        timeRemaining: calculateTimeRemaining(fallbackEndDate3),
        statusLabel: "Active Coverage",
        certificateUrl: "",
        covered: [
          "Accidental drops, impacts, cracks, and breakage",
          "Liquid spills and ingress damage",
          "Cracked screens or damaged outer casings from falls"
        ],
        notCovered: [
          "Theft, burglary or mysterious disappearance",
          "Normal wear and tear and cosmetic blemishes",
          "Intentional or deliberate misuse"
        ],
        startDate: fallbackStartDate,
        endDate: fallbackEndDate3
      }
    ];
  };

  const renderablePlans = getRenderablePlans();
  const totalPaid = order.product_price;
  const totalPlansCount = renderablePlans.length;
  const hasAnyActivePolicy = renderablePlans.some(p => p.statusLabel !== "Expired");

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="tw-bg-[#131313] tw-min-h-screen tw-p-6 md:tw-p-10 tw-font-sans tw-text-gray-200"
    >
      {/* ─── Breadcrumb and Top Download Header ─── */}
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <button
          onClick={() => navigate("/customer/orders")}
          className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-gray-400 hover:tw-text-white tw-bg-transparent tw-border-none tw-cursor-pointer tw-transition-colors"
        >
          <ChevronLeft className="tw-w-4 tw-h-4" />
          Back to Orders
        </button>

        <div className="tw-flex tw-items-center tw-gap-3">
          <span className="tw-bg-[#A3F3BE]/15 tw-text-[#A3F3BE] tw-text-[11px] tw-font-bold tw-px-3 tw-py-1 tw-rounded-full">
            {hasAnyActivePolicy ? "All Active Policy" : "No Active Policy"}
          </span>
          <button
            onClick={handleDownloadAllCertificates}
            className="tw-flex tw-items-center tw-gap-1.5 tw-bg-[#1c1c1e] hover:tw-bg-gray-800 tw-text-white tw-text-[11px] tw-font-bold tw-px-3.5 tw-py-2 tw-rounded-xl tw-border tw-border-gray-800 tw-cursor-pointer tw-transition-all"
          >
            <Download className="tw-w-3.5 tw-h-3.5" />
            Download All Certificates
          </button>
        </div>
      </div>

      {/* ─── Title Section ─── */}
      <div className="tw-mb-8">
        <h1 className="tw-text-3xl tw-font-extrabold tw-text-white tw-tracking-tight tw-mb-1">
          {order.product_title}
        </h1>
        <p className="tw-text-xs tw-text-gray-400">
          Order ID: <span className="tw-text-gray-300">{formattedOrderId}</span> • Purchased on {purchaseDateStr}
        </p>
      </div>

      {/* ─── Main Details Grid (Product Info & Order Summary) ─── */}
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6 tw-mb-10">
        {/* Left Card: Product Information */}
        <div className="tw-lg:col-span-2 tw-bg-[#1c1c1e] tw-border tw-border-gray-800/40 tw-rounded-3xl tw-p-6">
          <h2 className="tw-text-base tw-font-bold tw-text-white tw-mb-6">
            Product Information
          </h2>
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-6 tw-items-start">
            {/* White Square Product Box */}
            <div className="tw-w-32 tw-h-32 tw-bg-white tw-rounded-2xl tw-p-3 tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
              {order.product_image ? (
                <img
                  src={order.product_image}
                  alt={order.product_title}
                  className="tw-w-full tw-h-full tw-object-contain"
                />
              ) : (
                <Package className="tw-w-10 tw-h-10 tw-text-gray-400" />
              )}
            </div>

            {/* List details */}
            <div className="tw-flex-1 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4">
              <div className="tw-flex tw-items-start tw-gap-3">
                <Package className="tw-w-4 tw-h-4 tw-text-gray-400 tw-mt-0.5 tw-flex-shrink-0" />
                <div>
                  <div className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase">Product Type</div>
                  <div className="tw-text-xs tw-font-medium tw-text-gray-300 tw-mt-0.5">
                    {order.product_title.includes("Cabin") || order.product_title.includes("Luggage") ? "Aluminium Luggage (Cabin + Check-In Medium)" : "Consumer Goods"}
                  </div>
                </div>
              </div>

              <div className="tw-flex tw-items-start tw-gap-3">
                <Tag className="tw-w-4 tw-h-4 tw-text-gray-400 tw-mt-0.5 tw-flex-shrink-0" />
                <div>
                  <div className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase">Brand</div>
                  <div className="tw-text-xs tw-font-medium tw-text-gray-300 tw-mt-0.5">Protega Luggage</div>
                </div>
              </div>

              <div className="tw-flex tw-items-start tw-gap-3">
                <Calendar className="tw-w-4 tw-h-4 tw-text-gray-400 tw-mt-0.5 tw-flex-shrink-0" />
                <div>
                  <div className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase">Purchase Date</div>
                  <div className="tw-text-xs tw-font-medium tw-text-gray-300 tw-mt-0.5">{purchaseDateStr}</div>
                </div>
              </div>

              <div className="tw-flex tw-items-start tw-gap-3">
                <Hash className="tw-w-4 tw-h-4 tw-text-gray-400 tw-mt-0.5 tw-flex-shrink-0" />
                <div>
                  <div className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase">Order ID</div>
                  <div className="tw-text-xs tw-font-medium tw-text-gray-300 tw-mt-0.5">{formattedOrderId}</div>
                </div>
              </div>

              <div className="tw-flex tw-items-start tw-gap-3">
                <HelpCircle className="tw-w-4 tw-h-4 tw-text-gray-400 tw-mt-0.5 tw-flex-shrink-0" />
                <div>
                  <div className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase">Policy Status</div>
                  <div className="tw-mt-1">
                    <span className="tw-bg-[#A3F3BE]/20 tw-text-[#A3F3BE] tw-text-[9px] tw-font-bold tw-px-2 tw-py-0.5 tw-rounded">
                      {hasAnyActivePolicy ? "ACTIVE" : "EXPIRED"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Card: Order Summary */}
        <div className="tw-bg-[#1c1c1e] tw-border tw-border-gray-800/40 tw-rounded-3xl tw-p-6 tw-flex tw-flex-col tw-justify-between">
          <div>
            <h2 className="tw-text-base tw-font-bold tw-text-white tw-mb-6">
              Order Summary
            </h2>
            <div className="tw-space-y-4">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-blue-950/40 tw-flex tw-items-center tw-justify-center">
                  <span className="tw-text-blue-400 tw-text-xs tw-font-extrabold">₹</span>
                </div>
                <div>
                  <div className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase">Total Paid</div>
                  <div className="tw-text-sm tw-font-extrabold tw-text-white">₹ {totalPaid.toLocaleString()}</div>
                </div>
              </div>

              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-blue-950/40 tw-flex tw-items-center tw-justify-center">
                  <Shield className="tw-w-4 tw-h-4 tw-text-blue-400" />
                </div>
                <div>
                  <div className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase">Total Protection Plans</div>
                  <div className="tw-text-sm tw-font-extrabold tw-text-white">{totalPlansCount} Active</div>
                </div>
              </div>

              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-blue-950/40 tw-flex tw-items-center tw-justify-center">
                  <Calendar className="tw-w-4 tw-h-4 tw-text-blue-400" />
                </div>
                <div>
                  <div className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase">Protection Valid Up To</div>
                  <div className="tw-text-sm tw-font-extrabold tw-text-blue-400">{getLatestEndDate()}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="tw-mt-6 tw-bg-[#0F1E2E] tw-border tw-border-blue-950/60 tw-rounded-xl tw-p-3.5 tw-flex tw-items-start tw-gap-2.5">
            <Info className="tw-w-4 tw-h-4 tw-text-[#00C5FB] tw-mt-0.5 tw-flex-shrink-0" />
            <p className="tw-text-[11px] tw-text-gray-300 tw-leading-relaxed">
              You are fully protected with {totalPlansCount} active plans for this product.
            </p>
          </div>
        </div>
      </div>

      {/* ─── Active Protection Plans Accordion Section ─── */}
      <div className="tw-mb-10">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
          <div>
            <h2 className="tw-text-lg tw-font-extrabold tw-text-white">
              Your Active Protection Plans
            </h2>
            <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">
              {totalPlansCount} active plans protecting your product
            </p>
          </div>
          <button
            onClick={() => navigate(`/customer/return-chat/${order.order_id}?productId=${order.product_id}`)}
            className="tw-flex tw-items-center tw-gap-1.5 tw-bg-transparent hover:tw-bg-[#2c2c2e] tw-text-white tw-text-xs tw-font-bold tw-px-4 tw-py-2 tw-rounded-xl tw-border tw-border-gray-700 tw-cursor-pointer tw-transition-all"
          >
            <FileText className="tw-w-3.5 tw-h-3.5" />
            File a Claim
          </button>
        </div>

        <div className="tw-space-y-4">
          {renderablePlans.map((plan) => {
            const isExpanded = expandedPolicyId === plan.id;
            return (
              <div
                key={plan.id}
                className="tw-bg-[#1c1c1e] tw-border tw-border-gray-800/40 tw-rounded-3xl tw-overflow-hidden tw-transition-all"
              >
                {/* Header Row clickable to toggle */}
                <div
                  onClick={() => setExpandedPolicyId(isExpanded ? null : plan.id)}
                  className="tw-p-5 md:tw-p-6 tw-flex tw-flex-col md:tw-flex-row tw-items-start md:tw-items-center tw-justify-between tw-gap-4 tw-cursor-pointer hover:tw-bg-gray-800/20"
                >
                  <div className="tw-flex tw-items-start tw-gap-4">
                    <div className={`tw-w-10 tw-h-10 tw-rounded-xl ${plan.iconBg} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                      {plan.icon}
                    </div>
                    <div>
                      <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                        <h3 className="tw-text-sm md:tw-text-base tw-font-bold tw-text-white">
                          {plan.title}
                        </h3>
                        <span className={`tw-text-[8px] tw-font-extrabold tw-px-1.5 tw-py-0.5 tw-rounded ${
                          plan.isIncluded ? "tw-bg-gray-800 tw-text-gray-400" : "tw-bg-[#D97706]/25 tw-text-[#FBBF24]"
                        }`}>
                          {plan.typeBadge}
                        </span>
                      </div>
                      <p className="tw-text-xs tw-text-gray-400 tw-mt-1">
                        {plan.description}
                      </p>
                    </div>
                  </div>

                  <div className="tw-flex tw-items-center tw-gap-8 tw-w-full md:tw-w-auto tw-justify-between md:tw-justify-end">
                    <div className="tw-text-left md:tw-text-right">
                      <div className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase">Coverage Period</div>
                      <div className="tw-text-xs tw-font-medium tw-text-white tw-mt-0.5">{plan.coveragePeriod}</div>
                      <span className="tw-inline-block tw-mt-1 tw-bg-gray-800 tw-text-gray-400 tw-text-[9px] tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded">
                        {plan.durationTag}
                      </span>
                    </div>

                    <div className="tw-text-left md:tw-text-right">
                      {plan.policy_type === 'shipping' ? (
                        <>
                          <div className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase">Status</div>
                          <div className="tw-text-xs tw-font-bold tw-text-[#A3F3BE] tw-mt-0.5">{plan.statusLabel}</div>
                        </>
                      ) : (
                        <>
                          <div className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase">Time Remaining</div>
                          <div className="tw-text-xs tw-font-bold tw-text-[#A3F3BE] tw-mt-0.5">{plan.timeRemaining}</div>
                        </>
                      )}
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-2">
                      <span className="tw-bg-[#A3F3BE]/15 tw-text-[#A3F3BE] tw-text-[8px] tw-font-extrabold tw-px-1.5 tw-py-0.5 tw-rounded">
                        Active Policy
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="tw-w-4 tw-h-4 tw-text-gray-500" />
                      ) : (
                        <ChevronDown className="tw-w-4 tw-h-4 tw-text-gray-500" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="tw-px-6 tw-pb-6 tw-border-t tw-border-gray-800/40 tw-pt-6">
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6 tw-mb-6">
                      {/* What's Covered */}
                      <div className="tw-space-y-3">
                        <h4 className="tw-text-[10px] tw-font-extrabold tw-text-gray-500 tw-uppercase tw-tracking-wider">
                          What's Covered
                        </h4>
                        <ul className="tw-space-y-2.5 tw-list-none tw-p-0 tw-m-0">
                          {plan.covered.map((item, idx) => (
                            <li key={idx} className="tw-flex tw-items-start tw-gap-2 tw-text-xs tw-text-gray-300">
                              <CheckCircle2 className="tw-w-4 tw-h-4 tw-text-[#00C5FB] tw-flex-shrink-0 tw-mt-0.5" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* What's Not Covered */}
                      <div className="tw-space-y-3">
                        <h4 className="tw-text-[10px] tw-font-extrabold tw-text-gray-500 tw-uppercase tw-tracking-wider">
                          What's Not Covered
                        </h4>
                        <ul className="tw-space-y-2.5 tw-list-none tw-p-0 tw-m-0">
                          {plan.notCovered.map((item, idx) => (
                            <li key={idx} className="tw-flex tw-items-start tw-gap-2 tw-text-xs tw-text-gray-300">
                              <XCircle className="tw-w-4 tw-h-4 tw-text-[#D97706] tw-flex-shrink-0 tw-mt-0.5" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Plan Details */}
                      <div className="tw-bg-[#131313] tw-rounded-2xl tw-p-4 tw-space-y-4">
                        <h4 className="tw-text-[10px] tw-font-extrabold tw-text-gray-500 tw-uppercase tw-tracking-wider">
                          Plan Details
                        </h4>
                        <div className="tw-space-y-3">
                          <div className="tw-flex tw-items-center tw-gap-2.5">
                            <Calendar className="tw-w-3.5 tw-h-3.5 tw-text-gray-500" />
                            <div>
                              <div className="tw-text-[9px] tw-text-gray-500">Coverage Start Date</div>
                              <div className="tw-text-xs tw-text-gray-200">{formatDate(plan.startDate)}</div>
                            </div>
                          </div>

                          <div className="tw-flex tw-items-center tw-gap-2.5">
                            <Calendar className="tw-w-3.5 tw-h-3.5 tw-text-gray-500" />
                            <div>
                              <div className="tw-text-[9px] tw-text-gray-500">Coverage End Date</div>
                              <div className="tw-text-xs tw-text-gray-200">{formatDate(plan.endDate)}</div>
                            </div>
                          </div>

                          <div className="tw-flex tw-items-center tw-gap-2.5">
                            <Hash className="tw-w-3.5 tw-h-3.5 tw-text-gray-500" />
                            <div>
                              <div className="tw-text-[9px] tw-text-gray-500">Coverage ID</div>
                              <div className="tw-text-xs tw-text-gray-200">{plan.policy_number}</div>
                            </div>
                          </div>

                          <div className="tw-flex tw-items-center tw-gap-2.5">
                            <Download className="tw-w-3.5 tw-h-3.5 tw-text-gray-500" />
                            <div>
                              <div className="tw-text-[9px] tw-text-gray-500">Certificate</div>
                              {plan.certificateUrl ? (
                                <button
                                  onClick={() => handleDownloadSingleCertificate(plan.certificateUrl, plan.policy_number)}
                                  className="tw-text-xs tw-font-semibold tw-text-[#00C5FB] hover:tw-underline tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-left"
                                >
                                  Download Certificate
                                </button>
                              ) : (
                                <div className="tw-text-xs tw-text-gray-500">Not Available</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Important Info Bar */}
                    <div className="tw-bg-[#0F1E2E] tw-border tw-border-blue-950/60 tw-rounded-xl tw-p-3.5 tw-flex tw-items-center tw-gap-2.5">
                      <Info className="tw-w-4 tw-h-4 tw-text-[#00C5FB] tw-flex-shrink-0" />
                      <p className="tw-text-[11px] tw-text-gray-300">
                        <strong className="tw-text-white">Important:</strong> Keep your order invoice safe. You may need it while filing a claim.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Bottom Contact Support Panel ─── */}
      <div className="tw-bg-[#1c1c1e] tw-border tw-border-gray-800/40 tw-rounded-3xl tw-p-6 tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-4">
        <div className="tw-flex tw-items-center tw-gap-4">
          <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-800 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
            <Headphones className="tw-w-5 tw-h-5 tw-text-[#00C5FB]" />
          </div>
          <div>
            <h3 className="tw-text-sm md:tw-text-base tw-font-bold tw-text-white">
              Need help with your protection plans?
            </h3>
            <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">
              Chat with our support team or file a claim for any issues.
            </p>
          </div>
        </div>

        <button
          onClick={() => navigate("/customer/support")}
          className="tw-flex tw-items-center tw-gap-1.5 tw-bg-transparent hover:tw-bg-gray-800 tw-text-white tw-text-xs tw-font-bold tw-px-5 tw-py-3 tw-rounded-xl tw-border tw-border-gray-700 tw-cursor-pointer tw-transition-all"
        >
          <MessageSquare className="tw-w-4 tw-h-4" />
          Contact Support
        </button>
      </div>
    </motion.div>
  );
};

export default OrderDetails;